import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ArrowLeft, ArrowRight, Check, Rocket, GraduationCap, ShieldCheck, LineChart } from 'lucide-react';
import Button from '../ui/Button';
import { StepBudget, StepGoal, StepCampaigns, StepWork, GuardrailsDisclosure, SetupSummary } from './SetupSteps';
import { StepAudience } from './AudienceStep';
import { FormState, validate } from './ManagerForm';
import type { EligibleCampaign, Guardrails } from '../../services/trafficManagerService';

/**
 * Primeiro acesso: configuração em passos curtos, um assunto por vez, com o
 * resumo "Seu gestor" se montando ao lado conforme a pessoa escolhe.
 */
const SetupWizard: React.FC<{
  initial: FormState;
  eligible: EligibleCampaign[];
  saving: boolean;
  onSave: (f: FormState) => void;
}> = ({ initial, eligible, saving, onSave }) => {
  const { t } = useTranslation();
  const [f, setF] = useState<FormState>(initial);
  const [step, setStep] = useState(0);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((s) => ({ ...s, [k]: v }));
  const setG = <K extends keyof Guardrails>(k: K, v: Guardrails[K]) => setF((s) => ({ ...s, guardrails: { ...s.guardrails, [k]: v } }));

  const steps = [
    { label: t('trafficManager.setup.stepBudget', 'Verba'), title: t('trafficManager.setup.qBudget', 'Quanto você quer investir por mês?'), sub: t('trafficManager.setup.qBudgetSub', 'O total para as campanhas que ele vai gerir.') },
    { label: t('trafficManager.setup.stepGoal', 'Objetivo'), title: t('trafficManager.setup.qGoal', 'O que conta como resultado?'), sub: t('trafficManager.setup.qGoalSub', 'É por isso que ele julga cada campanha, palavra-chave e anúncio.') },
    { label: t('trafficManager.setup.stepCampaigns', 'Campanhas'), title: t('trafficManager.setup.qCampaigns', 'Quais campanhas ele gerencia?'), sub: t('trafficManager.setup.qCampaignsSub', 'Com os números reais de cada uma.') },
    { label: t('trafficManager.setup.stepAudience', 'Público'), title: t('trafficManager.setup.qAudience', 'Para quem e onde você vende?'), sub: t('trafficManager.setup.qAudienceSub', 'Opcional. Pule se o seu produto serve para todo mundo.') },
    { label: t('trafficManager.setup.stepWork', 'Como trabalhar'), title: t('trafficManager.setup.qWork', 'Como ele deve trabalhar?'), sub: t('trafficManager.setup.qWorkSub', 'Dá para mudar tudo depois.') }
  ];
  const last = step === steps.length - 1;
  const podeAvancar = step !== 0 || parseFloat(f.monthly_budget) > 0;

  const next = () => {
    if (!podeAvancar) { toast.error(t('trafficManager.form.budgetRequired', 'Informe quanto quer gastar por mês')); return; }
    if (!last) { setStep((s) => s + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    const err = validate(f, t);
    if (err) { toast.error(err); return; }
    onSave(f);
  };

  return (
    <div className="max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-white">{t('trafficManager.setup.title', 'Contrate seu gestor de tráfego')}</h1>
        <p className="mt-1.5 text-gray-500 dark:text-gray-400">{t('trafficManager.setup.subtitle5', 'Cinco passos. Ele começa pedindo sua aprovação para tudo.')}</p>
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-600 dark:text-gray-300">
          <span className="inline-flex items-center gap-1.5"><LineChart className="w-4 h-4 text-primary-600" />{t('trafficManager.setup.p1', 'Analisa a conta todo dia')}</span>
          <span className="inline-flex items-center gap-1.5"><GraduationCap className="w-4 h-4 text-primary-600" />{t('trafficManager.setup.p2', 'Mede o efeito de cada mudança')}</span>
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-primary-600" />{t('trafficManager.setup.p3', 'Nunca passa da verba do mês')}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start">
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 shadow-sm">
          <ol className="flex items-center gap-2 px-5 sm:px-8 pt-5 sm:pt-6" aria-label={t('trafficManager.setup.progress', 'Progresso') as string}>
            {steps.map((s, i) => {
              const done = i < step; const now = i === step;
              return (
                <li key={s.label} className="flex items-center gap-2 flex-1 min-w-0 last:flex-none">
                  <button type="button" onClick={() => (i <= step || podeAvancar) && setStep(i)} aria-current={now ? 'step' : undefined}
                    className="flex items-center gap-2 min-w-0">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-colors ${done ? 'bg-primary-600 text-white' : now ? 'bg-primary-600 text-white ring-4 ring-primary-100 dark:ring-primary-900/40' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'}`}>
                      {done ? <Check className="w-4 h-4" /> : i + 1}
                    </span>
                    <span className={`hidden sm:block text-sm truncate ${now ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-500'}`}>{s.label}</span>
                  </button>
                  {i < steps.length - 1 && <span className={`h-0.5 flex-1 rounded ${i < step ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'}`} />}
                </li>
              );
            })}
          </ol>

          <div className="px-5 sm:px-8 py-6 sm:py-8">
            <div className="mb-6">
              <div className="text-xs font-medium text-primary-600 mb-1">{t('trafficManager.setup.stepOf', 'Passo {{n}} de {{t}}', { n: step + 1, t: steps.length })}</div>
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">{steps[step].title}</h2>
              <p className="text-sm text-gray-500 mt-1">{steps[step].sub}</p>
            </div>
            {step === 0 && <StepBudget f={f} set={set} eligible={eligible} />}
            {step === 1 && <StepGoal f={f} set={set} />}
            {step === 2 && <StepCampaigns f={f} set={set} eligible={eligible} />}
            {step === 3 && <StepAudience f={f} setG={setG} />}
            {step === 4 && <div className="space-y-6"><StepWork f={f} set={set} /><GuardrailsDisclosure f={f} setG={setG} /></div>}
          </div>

          <div className="flex items-center justify-between gap-3 px-5 sm:px-8 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 rounded-b-2xl">
            <Button type="button" variant="ghost" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} leftIcon={<ArrowLeft className="w-4 h-4" />}>
              {t('trafficManager.setup.back', 'Voltar')}
            </Button>
            <Button type="button" onClick={next} isLoading={saving} disabled={!podeAvancar} size="lg"
              rightIcon={last ? <Rocket className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}>
              {last ? t('trafficManager.activate', 'Ativar gestor de tráfego') : t('trafficManager.setup.next', 'Continuar')}
            </Button>
          </div>
        </div>

        <aside className="lg:sticky lg:top-6 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-5 shadow-sm">
          <SetupSummary f={f} eligible={eligible} />
          <p className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 leading-relaxed">
            {f.mode === 'approval'
              ? t('trafficManager.setup.footApproval', 'Nada muda nas suas contas sem você aprovar. Você pode ligar o piloto automático quando confiar nele.')
              : t('trafficManager.setup.footAutopilot', 'Ele executa sozinho dentro das travas, e você acompanha cada mudança com o motivo e o efeito medido.')}
          </p>
        </aside>
      </div>
    </div>
  );
};

export default SetupWizard;
